import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';

import { AuthService } from './auth.service';

/** In-app route where a signed-in user chooses which account to work in. */
export const ACCOUNT_SWITCHER_URL = '/account/switch';

/**
 * Require a determined active account. A user whose session carries no Auth0
 * organization (so `org_id` is absent) and who belongs to more than one account
 * has nothing selected yet — send them to the switcher to choose one; picking an
 * organization account triggers the org-scoped Auth0 login.
 *
 * A user who belongs to **zero** accounts is a valid, supported state (account
 * membership is not required to use Growzz) — they are let through rather than
 * trapped on the switcher's empty state. A lone account is auto-selected inside
 * `ensureActiveAccount()`, so only genuine multi-account users ever reach the
 * switcher, mirroring the top-nav "Change account" link's `hasMultipleAccounts`
 * gate.
 *
 * Composed onto the routes that need an account. The switcher route itself
 * deliberately omits this guard so the user can actually reach it (and so it
 * never redirects onto itself). Runs after `authGuard`, so a session is assured.
 */
export const accountGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const accountId = await auth.ensureActiveAccount();
  if (accountId !== null) return true;

  // Active account undetermined. `ensureActiveAccount()` loaded the accounts list
  // on this path, so `hasMultipleAccounts()` is accurate here. Only force a choice
  // when the user genuinely belongs to more than one account; zero accounts falls
  // through and proceeds.
  return auth.hasMultipleAccounts() ? router.parseUrl(ACCOUNT_SWITCHER_URL) : true;
};

/**
 * Gate the account switcher route itself: it is only meaningful when the user can
 * actually switch — i.e. belongs to more than one account. Users with zero or one
 * account have nothing to choose, so redirect them home (their `accountGuard` on
 * `/` will not bounce them back, since it also only routes to the switcher when
 * there is more than one account). Runs after `authGuard`, so a session is assured.
 */
export const accountSwitcherGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  try {
    await auth.ensureAccountsLoaded();
  } catch {
    // Don't strand the user on the switcher if their accounts failed to load;
    // send them home and let the shell guards re-decide.
    return router.parseUrl('/');
  }

  return auth.hasMultipleAccounts() ? true : router.parseUrl('/');
};
